import { Form, FormRenderProps } from 'react-final-form';
import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { FormStyled } from './styled';
import { FilterButtons } from './FilterButtons';
import {
  adaptFiltersForState,
  convertSearchValues,
  fromObjToUrl
} from '../../../utils/helpers';
import { SortOption } from '../../../graphql/types';
import { GetPashaFilterDataQuery } from '../../../graphql/graphql';
import { getInitialCategoryId } from '../../../utils/helpers/search';
import { NewSearchBarPlaceholder } from '../../Placeholders/NewSearchBarPlaceholder';

const SearchPageFilters = dynamic(
  () =>
    import('../../NewSearch/NewSearchBar/SearchPageFilters').then(
      mod => mod.SearchPageFilters
    ),
  {
    loading: NewSearchBarPlaceholder,
    ssr: false
  }
);

interface FilterPanel {
  filtersParams: GetPashaFilterDataQuery;
  filterValues: unknown;
  currSortOption: SortOption;
  setFilterValues: (filters: unknown) => void;
}
export const FilterPanel: React.FC<FilterPanel> = ({
  filtersParams,
  filterValues,
  currSortOption,
  setFilterValues
}) => {
  const router = useRouter();
  const onSubmit = useCallback(
    values => {
      setFilterValues(convertSearchValues(values));

      const { href, as } = fromObjToUrl(
        values,
        filtersParams,
        router.query?.sorting,
        true
      );

      const queryStringPart = href.split('?')[1];
      const query = queryStringPart
        ? Object.fromEntries(new URLSearchParams(queryStringPart))
        : {};

      router.replace({ pathname: router.pathname, query }, as, {
        shallow: true
      });
    },
    [filtersParams, router, setFilterValues]
  );

  const defaultCategoryId = useMemo(
    () => getInitialCategoryId(filtersParams.categories || []),
    [filtersParams.categories]
  );

  const initialValues = useMemo(() => {
    const base =
      (adaptFiltersForState(filterValues) as Record<string, unknown>) || {};
    return {
      ...base,
      category_id: base.category_id ?? defaultCategoryId,
      sorting: currSortOption?.id
    };
  }, [filterValues, currSortOption?.id, defaultCategoryId]);

  return (
    <Form
      onSubmit={onSubmit}
      subscription={{
        submitting: true,
        values: true
      }}
      initialValues={initialValues}
    >
      {(renderProps: FormRenderProps<typeof initialValues>) => {
        return (
          <FormStyled
            method="get"
            id="new_search"
            acceptCharset="UTF-8"
            noValidate={true}
            onSubmit={e => {
              e.preventDefault();
              renderProps.handleSubmit();
            }}
          >
            <SearchPageFilters
              isPashaLanding
              categories={filtersParams.categories}
              additionalButtons={
                <FilterButtons
                  showValueInInput={false}
                  showIcon={false}
                  initialSort={currSortOption?.id}
                  setFilterValues={setFilterValues}
                  defaultCategoryId={defaultCategoryId}
                />
              }
            />
          </FormStyled>
        );
      }}
    </Form>
  );
};
